/**
 * Удаляет IndexedDB; `deleteDatabase` не возвращает Promise — оборачиваем для await.
 */
const deleteIndexedDb = (databaseName: string): Promise<void> =>
    new Promise((resolve, reject) => {
        const req = indexedDB.deleteDatabase(databaseName);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error ?? new Error("IDB delete failed"));
        // Другая вкладка держит БД — не блокируем очистку навсегда
        req.onblocked = () => resolve();
    });

/**
 * Снимает регистрацию SW — иначе precache не перезапустится после ручного caches.delete().
 */
const unregisterAllServiceWorkers = async (): Promise<void> => {
    if (!("serviceWorker" in navigator)) {
        return;
    }
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((reg) => reg.unregister()));
};

/**
 * Очищает Cache Storage, IndexedDB Workbox и снимает SW, затем опционально перезагружает страницу.
 * @param withReload - перезагрузить страницу после очистки
 */
export async function clearAllCaches(withReload: boolean) {
    window.sessionStorage.clear();
    const cacheNames = await caches.keys();
    for (const name of cacheNames) {
        await caches.delete(name);
    }
    await deleteIndexedDb("workbox-expiration");
    await unregisterAllServiceWorkers();
    if (withReload) {
        window.location.reload();
    }
}
/**
 * возвращает размер всех кэшей в байтах
 * @returns размер всех кэшей в байтах
 */
export async function getCachesSize() {
    const cacheNames = await caches.keys();
    const totalSizeArray = await Promise.all(
        cacheNames.map((name) => caches.open(name).then((cache) => cacheSize(cache)))
    );
    return totalSizeArray.reduce((acc, n) => (acc || 0) + (n || 0), 0);
}

// Returns approximate size of a single cache (in bytes)
async function cacheSize(cache: Cache) {
    const requests = await cache.keys();
    const sizes = await Promise.all(
        requests.map((req) =>
            cache.match(req).then((res) =>
                res
                    ?.clone()
                    .blob()
                    .then((b) => b.size)
            )
        )
    );
    return sizes.reduce((acc, n) => (acc || 0) + (n || 0), 0);
}
/**
 * возвращает размер хранилища в байтах
 */
export async function getStorageSize() {
    if (navigator.storage && navigator.storage.estimate) {
        const quota = await navigator.storage.estimate();
        const remaining = (quota.quota || 0) - (quota.usage || 0);
        return remaining;
    }
}
